import {
  AbstractMesh,
  AnimationGroup,
  Color3,
  Mesh,
  MeshBuilder,
  Observer,
  PBRMaterial,
  Scene,
  SceneLoader,
  StandardMaterial,
  Vector3,
} from "@babylonjs/core";
import "@babylonjs/loaders/glTF";
import { clampMaterialLights } from "../world/lighting.js";
import { getSharedMaterials } from "../world/materials.js";

/** Bundled Quaternius Ultimate Animated Character Pack casuals (CC0,
 *  quaternius.com), rigged and animated GLBs in client/public/models/avatars.
 *  Modern mall-goers, replacing the KayKit fantasy adventurers that clashed
 *  with the believable-mall art direction. */
export const AVATAR_VARIANTS: string[] = [
  "Casual_Male",
  "Casual_Female",
  "Casual2_Male",
  "Casual2_Female",
  "Casual3_Male",
  "Casual3_Female",
];

const AVATAR_HEIGHT_M: number = 1.7;
/** Consider the avatar walking if a move arrived within this window. */
const WALK_HOLD_MS: number = 300;

/** Deterministic 32-bit hash of a session id (variant + palette picks). */
export function hashSessionId(sessionId: string): number {
  let hash: number = 0;
  for (let i: number = 0; i < sessionId.length; i++) {
    hash = (hash * 31 + sessionId.charCodeAt(i)) >>> 0;
  }
  return hash;
}

/** Same session id always gets the same character, spread across variants. */
export function pickAvatarVariant(sessionId: string): string {
  return AVATAR_VARIANTS[hashSessionId(sessionId) % AVATAR_VARIANTS.length];
}

export interface AvatarPalette {
  skin: string;
  shirt: string;
  pants: string;
  belt: string;
  hair: string;
}

const SKIN_TONES: string[] = ["#f2c69b", "#e0ac7e", "#c68e63", "#9c6b47", "#7a4f33", "#5c3b26"];
const SHIRT_COLORS: string[] = ["#5b7fb4", "#b45b5b", "#6aa06a", "#8d6bb4", "#b4975b", "#4f8f8f", "#b46b93", "#6d7787"];
const PANTS_COLORS: string[] = ["#3f4a5a", "#5a4a3f", "#37472f", "#46465a", "#3a3a3a", "#6e5a46"];
/** Paper shopping bags carried by simulated shoppers. */
const BAG_COLORS: string[] = ["#c8543f", "#3f6cc8", "#d9b23f", "#4f9e5e", "#8c4fa0", "#e2e2e2"];
const HAIR_COLORS: string[] = ["#2b2118", "#4a301c", "#7a5230", "#a0743c", "#b9b3a8", "#23232a", "#7e2f22"];

/** The Nov 2019 Quaternius exports ship near-black material colors (meant
 *  for runtime tinting), so every remote player gets a deterministic outfit:
 *  same session id, same look, and different offsets per slot so the combos
 *  spread widely across players. */
export function pickAvatarPalette(sessionId: string): AvatarPalette {
  const hash: number = hashSessionId(sessionId);
  return {
    skin: SKIN_TONES[hash % SKIN_TONES.length],
    shirt: SHIRT_COLORS[(hash >>> 3) % SHIRT_COLORS.length],
    pants: PANTS_COLORS[(hash >>> 6) % PANTS_COLORS.length],
    belt: "#5a4432",
    hair: HAIR_COLORS[(hash >>> 9) % HAIR_COLORS.length],
  };
}

/** What an avatar is doing. Players never send this; simulated shoppers do. */
export type AvatarActivity = "walk" | "browse" | "sit";

/**
 * One remote player's in-world presence. Starts as the old colored capsule
 * so the player is visible instantly, then swaps to a rigged character model
 * (idle/walk animations driven by incoming move packets) once the GLB loads.
 */
export class RemotePlayer {
  private readonly scene: Scene;
  private readonly sessionId: string;
  private placeholder: Mesh | undefined;
  private blobShadow: Mesh | undefined;
  private modelRoot: AbstractMesh | undefined;
  private idleAnim: AnimationGroup | undefined;
  private walkAnim: AnimationGroup | undefined;
  private sitAnim: AnimationGroup | undefined;
  private walking: boolean = false;
  /** Set for server-driven NPCs; real players stay undefined and keep the
   *  move-recency heuristic (their packets carry no activity). */
  private activity: AvatarActivity | undefined;
  private bag: Mesh | undefined;
  private lastMoveAtMs: number = 0;
  private tickObserver: Observer<Scene> | null = null;
  private disposed: boolean = false;
  private position: Vector3;
  private rotationY: number = 0;

  constructor(scene: Scene, sessionId: string, x: number, y: number, z: number) {
    this.scene = scene;
    this.sessionId = sessionId;
    this.position = new Vector3(x, y, z);

    const capsule: Mesh = MeshBuilder.CreateCapsule(
      `remote-${sessionId}`,
      { radius: 0.3, height: 1.7 },
      scene
    );
    capsule.position = new Vector3(x, y + 0.85, z);
    const mat: StandardMaterial = new StandardMaterial(`remoteMat-${sessionId}`, scene);
    const hash: number = parseInt(sessionId.substring(0, 6), 36) & 0xffffff;
    mat.diffuseColor = Color3.FromHexString(
      "#" + (hash | 0x808080).toString(16).padStart(6, "0").slice(-6)
    );
    capsule.material = mat;
    this.placeholder = capsule;

    // Cheap grounding: avatars are the only moving things, so they skip the
    // static shadow map entirely and carry a soft blob decal instead.
    const blob: Mesh = MeshBuilder.CreateGround(
      `remote-shadow-${sessionId}`, { width: 0.9, height: 0.9 }, scene
    );
    blob.material = getSharedMaterials(scene).contactShadow;
    blob.isPickable = false;
    blob.position = new Vector3(x, y + 0.02, z);
    this.blobShadow = blob;

    void this.loadModel();
  }

  setTransform(x: number, y: number, z: number, rotationY: number): void {
    this.position.set(x, y, z);
    this.rotationY = rotationY;
    this.lastMoveAtMs = performance.now();
    this.applyTransform();
  }

  /**
   * Explicit animation state for server-driven shoppers. Calling this once
   * takes the avatar off the move-recency heuristic for good.
   */
  setActivity(activity: AvatarActivity): void {
    if (this.activity === activity) {
      return;
    }
    this.activity = activity;
    this.applyActivity();
  }

  /** Shows or hides the shopping bag in the avatar's hand. */
  setBag(carrying: boolean): void {
    if (carrying === (this.bag !== undefined)) {
      return;
    }
    if (!carrying) {
      this.bag?.dispose();
      this.bag = undefined;
      return;
    }
    const bag: Mesh = MeshBuilder.CreateBox(
      `avatar-bag-${this.sessionId}`, { width: 0.26, height: 0.32, depth: 0.14 }, this.scene
    );
    const mat: StandardMaterial = new StandardMaterial(`avatar-bag-mat-${this.sessionId}`, this.scene);
    mat.diffuseColor = Color3.FromHexString(
      BAG_COLORS[hashSessionId(this.sessionId) % BAG_COLORS.length]
    );
    mat.specularColor = new Color3(0.05, 0.05, 0.05);
    bag.material = mat;
    bag.isPickable = false;
    // Parented to the model root, not a hand bone: the packs' bone names
    // differ per variant, and a bag hanging at hip height beside the body
    // reads correctly at mall distances.
    bag.position = new Vector3(0.26, 0.62, 0.02);
    this.bag = bag;
    if (this.modelRoot !== undefined) {
      bag.parent = this.modelRoot;
    }
  }

  dispose(): void {
    this.disposed = true;
    if (this.tickObserver !== null) {
      this.scene.onBeforeRenderObservable.remove(this.tickObserver);
      this.tickObserver = null;
    }
    this.placeholder?.dispose();
    this.placeholder = undefined;
    this.blobShadow?.dispose();
    this.blobShadow = undefined;
    this.bag?.dispose();
    this.bag = undefined;
    this.sitAnim?.dispose();
    this.idleAnim?.dispose();
    this.walkAnim?.dispose();
    this.modelRoot?.dispose(false, true);
    this.modelRoot = undefined;
  }

  /** First exact-name match, else first group whose name contains a wanted
   *  token: keeps the class working across packs with different clip names
   *  (KayKit used "Walking_A", Quaternius uses "Walk"). */
  private static pickAnimation(
    groups: AnimationGroup[],
    wanted: string[]
  ): AnimationGroup | undefined {
    for (const want of wanted) {
      const exact: AnimationGroup | undefined = groups.find(
        (g) => g.name.toLowerCase() === want
      );
      if (exact !== undefined) {
        return exact;
      }
    }
    return groups.find((g) => wanted.some((want) => g.name.toLowerCase().includes(want)));
  }

  private async loadModel(): Promise<void> {
    const variant: string = pickAvatarVariant(this.sessionId);
    try {
      const result = await SceneLoader.ImportMeshAsync(
        "",
        "/models/avatars/",
        `${variant}.glb`,
        this.scene
      );
      if (this.disposed) {
        for (const m of result.meshes) {
          m.dispose(false, true);
        }
        for (const g of result.animationGroups) {
          g.dispose();
        }
        return;
      }
      const root: AbstractMesh = result.meshes[0];
      root.name = `remote-model-${this.sessionId}`;

      // Normalize to human height regardless of the source model's scale.
      const bounds = root.getHierarchyBoundingVectors(true);
      const height: number = bounds.max.y - bounds.min.y;
      if (height > 0) {
        const s: number = AVATAR_HEIGHT_M / height;
        root.scaling.scaleInPlace(s);
      }
      // Drive yaw with plain euler rotation (glTF import sets a quaternion).
      root.rotationQuaternion = null;
      const palette: AvatarPalette = pickAvatarPalette(this.sessionId);
      const tintByMaterial: Record<string, string> = {
        Skin: palette.skin,
        Shirt: palette.shirt,
        Pants: palette.pants,
        Belt: palette.belt,
        Hair: palette.hair,
      };
      for (const mesh of result.meshes) {
        const mat = mesh.material;
        if (mat instanceof PBRMaterial) {
          mat.usePhysicalLightFalloff = false;
          const tint: string | undefined = tintByMaterial[mat.name];
          if (tint !== undefined) {
            mat.albedoColor = Color3.FromHexString(tint).toLinearSpace();
          }
        }
      }
      // MUST run after every avatar load: the glTF loader just raised
      // maxSimultaneousLights on ALL scene materials to the mall's full
      // light count, which overflows mobile vertex uniform-buffer limits on
      // skinned meshes and leaves OTHER concurrently-loaded avatars
      // invisible. The clamp here re-fixes every material, including ones a
      // sibling load raised after its own clamp ran.
      clampMaterialLights(this.scene);

      for (const group of result.animationGroups) {
        group.stop();
      }
      this.idleAnim = RemotePlayer.pickAnimation(result.animationGroups, ["idle"]);
      this.walkAnim = RemotePlayer.pickAnimation(result.animationGroups, ["walk", "walking_a"]);
      this.sitAnim = RemotePlayer.pickAnimation(result.animationGroups, ["sit"]);
      this.idleAnim?.play(true);

      this.modelRoot = root;
      if (this.bag !== undefined) {
        this.bag.parent = root;
      }
      this.placeholder?.dispose();
      this.placeholder = undefined;
      this.applyTransform();

      if (this.activity !== undefined) {
        this.applyActivity();
      }

      // Fall back to idle shortly after move packets stop arriving.
      this.tickObserver = this.scene.onBeforeRenderObservable.add(() => {
        if (this.activity !== undefined) {
          return;
        }
        const moving: boolean = performance.now() - this.lastMoveAtMs < WALK_HOLD_MS;
        if (moving !== this.walking) {
          this.walking = moving;
          if (moving) {
            this.idleAnim?.stop();
            this.walkAnim?.play(true);
          } else {
            this.walkAnim?.stop();
            this.idleAnim?.play(true);
          }
        }
      });
    } catch (err: unknown) {
      // Keep the capsule placeholder; a missing model must never break play.
      console.error(`[RemotePlayer ${this.sessionId}] avatar load failed:`, err);
    }
  }

  /** Plays the clip for the current explicit activity. Sit falls back to
   *  idle: not every Quaternius casual ships a sitting clip. */
  private applyActivity(): void {
    this.idleAnim?.stop();
    this.walkAnim?.stop();
    this.sitAnim?.stop();
    if (this.activity === "walk") {
      (this.walkAnim ?? this.idleAnim)?.play(true);
      return;
    }
    if (this.activity === "sit") {
      (this.sitAnim ?? this.idleAnim)?.play(true);
      return;
    }
    this.idleAnim?.play(true);
  }

  private applyTransform(): void {
    if (this.modelRoot !== undefined) {
      // Model origin is at the feet; capsule origin was at its center.
      this.modelRoot.position.set(this.position.x, this.position.y, this.position.z);
      // Character packs author facing +Z; our rotation convention needs the flip.
      this.modelRoot.rotation.y = this.rotationY + Math.PI;
    }
    if (this.placeholder !== undefined) {
      this.placeholder.position.set(this.position.x, this.position.y + 0.85, this.position.z);
      this.placeholder.rotation.y = this.rotationY;
    }
    if (this.blobShadow !== undefined) {
      this.blobShadow.position.set(this.position.x, this.position.y + 0.02, this.position.z);
    }
  }
}
